import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Container, Typography, Box, alpha, useTheme,
} from '@mui/material';
import { keyframes } from '@mui/material/styles'
import { motion } from 'framer-motion'
import PortfolioPopUp from '@pages/PortfolioPopUp.jsx'
import VideoPlayer from '@pages/VideoPlayer.jsx'

const Slideshow = React.memo(function Slideshow ({ images, height = { xs: '60vh', md: '80vh' } }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [prevIndex, setPrevIndex] = useState(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [loaded, setLoaded] = useState(new Set())

  const intervalRef = useRef(null)
  const timeoutRef = useRef(null)

  const preload = useCallback((idx) => {
    if (!images?.length) return
    const i = ((idx % images.length) + images.length) % images.length
    const src = images[i]?.src
    if (!src || loaded.has(src)) return
    const img = new Image()
    img.src = src
    img.onload = () => setLoaded(prev => new Set(prev).add(src))
  }, [images, loaded]);

  useEffect(() => {
    if (!images?.length) return
    preload(0)
    preload(1)
    preload(2)
  }, [images, preload]);

  useEffect(() => {
    if (!images || images.length <= 1) return

    const start = () => {
      setPrevIndex(activeIndex)
      const next = (activeIndex + 1) % images.length
      preload(next)
      setIsTransitioning(true)

      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false)
        setPrevIndex(null)
      }, 1200);

      setActiveIndex(next)
    };

    intervalRef.current = setInterval(start, 6000)

    return () => {
      clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    };
  }, [images, activeIndex, preload]);

  const current = images?.[activeIndex]
  const previous = prevIndex != null ? images?.[prevIndex] : null

  return (<Box sx={{ position: 'relative', width: '100%', height }}>
     {previous && (<Box
       key={previous.src}
       component="img"
       src={previous.src}
       alt={previous.name}
       loading="lazy"
       sx={{
         position:  'absolute',
         inset:     0,
         width:     '100%',
         height:    '100%',
         objectFit: 'cover',
         opacity:   isTransitioning ? 0 : 1,
         zIndex:    0,
         transition: 'opacity 1.2s cubic-bezier(0.4,0,0.2,1)',
         transformOrigin: 'center',
         willChange: 'opacity, transform',
         filter:    'brightness(0.85)'
       }}
      />)}

     {current && (<Box
       key={current.src}
       component="img"
       src={current.src}
       alt={current.name}
       loading="lazy"
       sx={{
         position:  'absolute',
         inset:     0,
         width:     '100%',
         height:    '100%',
         objectFit: 'cover',
         opacity:   1,
         zIndex:    1,
         transition: 'opacity 1.2s cubic-bezier(0.4,0,0.2,1), transform 1.2s',
         transform: isTransitioning
                     ? 'scale(1.06)'
                     : 'scale(1.02)',
         willChange: 'opacity, transform',
         filter:    'brightness(0.9)'
       }}
      />)}
   </Box>);
});

const ProjectsPage = () => {
  const theme = useTheme()


  const allImages = useMemo(() => {
    const modules = import.meta.glob('/public/web/*.JPG', { eager: true })
    const imgs = Object.keys(modules).map(path => {
      const src = path.startsWith('/public') ? path.replace('/public', '') : path
      return { src, name: path.split('/').pop() }
    });
    for (let i = imgs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [imgs[i], imgs[j]] = [imgs[j], imgs[i]]
    }
    return imgs
  }, []);

  const [open, setOpen] = useState(false)
  const [lightboxSrc, setLightboxSrc] = useState(null)

  const videoControlsRef = useRef(null)

  const openLightbox = (src) => {

    if (videoControlsRef.current?.pause) videoControlsRef.current.pause()
    setLightboxSrc(src)
    setOpen(true)
  };
  const closeLightbox = () => {
    setOpen(false)
    setLightboxSrc(null)

    if (videoControlsRef.current?.resume) videoControlsRef.current.resume()
  };

  const slowRotate = keyframes`
      0% {
          transform: scale(1) rotate(-3deg);
      }
      100% {
          transform: scale(1.08) rotate(3deg);
      }
  `;

  return (<Box sx={{
     minHeight: '100vh',
     position: 'relative',
     overflow: 'hidden',
     background: `linear-gradient(135deg, ${alpha('#0e0202', 0.85)} 0%, ${alpha('#2C3E50', 0.6)} 100%)`,
     color:    '#fff'
   }}>
     <Box sx={{
       position:       'absolute',
       top:            '-10%',
       left:           '-10%',
       right:          '-10%',
       bottom:         '-10%',
       backgroundImage: 'url(/images/3dinterior.webp)',
       backgroundSize: 'cover',
       backgroundPosition: 'center',
       filter:         'blur(6px) saturate(0.9) contrast(0.9)',
       opacity:        0.28,
       zIndex:         0,
       transformOrigin: '50% 50%',
       animation:      `${slowRotate} 18s ease-in-out infinite alternate`,
       pointerEvents:  'none'
     }}/>

     <Box sx={{
       position: 'absolute',
       inset:    0,
       background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.25) 100%)',
       zIndex:   2,
       pointerEvents: 'none'
     }}/>

     <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, py: { xs: 6, md: 10 } }}>
       <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
         <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
           <Typography variant="h1" sx={{
             fontFamily: '"Playfair Display", Georgia, serif',
             fontWeight: 400, fontSize: { xs: '3.2rem', md: '5rem', lg: '6rem' }, color: '#fff',
             lineHeight: 1.05,
             letterSpacing: '-0.02em'
           }}>
             Portfolio
           </Typography>
           <Typography variant="subtitle1" sx={{
             fontFamily: '"Fascinate", fantasy',
             mt:         2,
             color:      alpha('#fff', 0.85),
             maxWidth:   900,
             mx:         'auto'
           }}>
             Explore the curated collection of projects that balance craft, comfort, and clarity.
           </Typography>
         </motion.div>
       </Box>





       <Box sx={{ mb: { xs: 4, md: 6 }, borderRadius: 2, overflow: 'hidden' }}>
         <Slideshow images={allImages} height={{ xs: '40vh', md: '50vh' }}/>
       </Box>


       <Box sx={{
         display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 3 }
       }}>
         {allImages.map((img, i) => (<motion.div
           key={img.src}
           initial={{ opacity: 0, y: 18, scale: 0.98 }}
           whileInView={{ opacity: 1, y: 0, scale: 1 }}
           viewport={{ once: true }}
           transition={{ duration: 0.6, delay: i * 0.04 }}
           style={{ width: '100%' }}
          >
            <Box
             component="img"
             src={img.src}
             alt={img.name}
             loading="lazy"
             onClick={() => openLightbox(img.src)}
             sx={{
               width:      '100%',
               height:     { xs: 220, sm: 260, md: 320 },
               objectFit:  'cover',
               borderRadius: 2,
               boxShadow:  '0 8px 30px rgba(2,8,23,0.45)',
               cursor:     'pointer',
               transformOrigin: 'center',
               transition: 'transform 0.6s cubic-bezier(0.2,0.8,0.2,1), box-shadow 0.3s',
               '&:hover':  {
                 transform: 'scale(1.04) translateY(-6px)', boxShadow: '0 18px 50px rgba(2,8,23,0.6)'
               }
             }}
            />
          </motion.div>))}
       </Box>
     </Container>


     <PortfolioPopUp
      open={open}
      onClose={closeLightbox}
      initialSrc={lightboxSrc}
      images={allImages}
     />
   </Box>);
};

export default ProjectsPage
